import { useApolloClient } from "@apollo/react-hooks";
import { useCookies } from "react-cookie";

const TOKEN_NAME = "sessionToken_cass_web";

// Custom hook to handle authToken - use composition to decouple the auth system and its storage
export const useAuthToken = () => {
  const [cookies, setCookie, removeCookie] = useCookies([TOKEN_NAME]);

  const setAuthToken = (authToken, expiry) => {
    // First, set the cookie if cookies are available
    if (navigator.cookieEnabled) {
      setCookie(TOKEN_NAME, authToken, {
        expires: expiry,
        sameSite: "none",
        secure: true,
      });
    } else {
      // Fallback to localStorage if cookies are disabled
      localStorage.setItem(TOKEN_NAME, JSON.stringify({ authToken, expiry }));
      window.location.reload();
    }
    // Optionally reload the page after setting the token
  };

  const getAuthToken = () => {
    // Try to get the token from cookies first
    if (cookies[TOKEN_NAME]) {
      return cookies[TOKEN_NAME];
    } else {
      // If not found in cookies, fall back to localStorage
      const storedToken = localStorage.getItem(TOKEN_NAME);
      if (storedToken) {
        const { authToken, expiry } = JSON.parse(storedToken);
        const tokenExpiry = new Date(expiry);
        // Check if token has expired
        if (new Date() < tokenExpiry) {
          return authToken;
        } else {
          localStorage.removeItem(TOKEN_NAME); // Remove expired token
        }
      }
    }
    return null; // No valid token found
  };

  const removeAuthToken = () => {
    // Remove the token from both cookies and localStorage
    removeCookie(TOKEN_NAME);
    localStorage.removeItem(TOKEN_NAME);
  };

  return [getAuthToken(), setAuthToken, removeAuthToken];
};

// Custom hook to handle user logout
export const useLogout = () => {
  const [, , removeAuthToken] = useAuthToken();
  const apolloClient = useApolloClient();

  const logout = async () => {
    await apolloClient.clearStore(); // Clear Apollo client store
    removeAuthToken(); // Remove auth token
  };

  return logout;
};
